/* eslint-disable jsx-a11y/anchor-is-valid */

import { Alert, Button, Card, Tooltip } from '@pmi/dsm-react';
import {
  AlertErrorSolidIcon,
  AlertInfoSolidIcon,
  AlertSuccessSolidIcon,
  AlertWarningSolidIcon,
} from '@pmi/dsm-react/dist/components/icons';
import { EditIcon } from '@pmi/dsm-react/dist/components/icons/fa/16';
import { ReactNode, useState } from 'react';
import { useSelector } from 'react-redux';
import { toastWithAnalytics } from '../../../shared/analytics';
import InviteWizard from '../../../shared/components/InviteWizard';
import { mapPaymentArrangementText } from '../../../shared/constants';
import { buildTrainingHubUrl, toDayMonthYear } from '../../../shared/helpers';
import usePermissions from '../../../shared/hooks/usePermissions';
import { AgreementApiModel } from '../../../shared/models/DealModel';
import PERMISSIONS from '../../../shared/permissions';
import { accountSelector } from '../../../shared/store/shared/accountSlice';
import checkExpiryStatus from '../../../shared/utils/checkExpiryStatus';
import formatCurrency from '../../../shared/utils/formatCurrency';
import printableZoneRestrictionLabel, {
  printableZonesLabel,
} from '../../../shared/utils/printableZoneRestrictionLabel';
import useDealTotalSpend from '../useDealTotalSpend';
import Addendums from '../Addendums';
import styles from './dealDetails.module.scss';
import DealProductCards from './DealProductCards';
import DealProductsTable from './DealProductsTable';
import EditAgreementNameModal from './EditAgreementNameModal';
import EditPointOfContactModal from './EditPointOfContactModal';
import ExpireWarningModal from './ExpireWarningModal';
import { useGetAccountDataQuery } from '../../../shared/navigation/apiSlice';
import { AtpStatusType } from '../../../shared/models/AccountModel';
import getAgreementAlertType from '../../../shared/utils/agreementAlert';

function SmallCard({
  label,
  value,
  labelPreValue = null,
  labelTooltip = null,
  isExpiring = false,
  isExpired = false,
  className = undefined,
}: {
  label: string;
  value: string;
  labelPreValue?: ReactNode;
  labelTooltip?: string;
  isExpiring?: boolean;
  isExpired?: boolean;
  className?: string | undefined;
}) {
  const isEnabledStatus = label === 'Status' && value === 'Enabled';
  const isDisabledStatus = label === 'Status' && value === 'Disabled';
  const isWithin30DaysStatus = label === 'Expires soon' && isExpiring;
  const isExpiredStatus = label === 'Expired' && isExpired;

  return (
    <Card
      className={`h-100 w-100 ${styles.smallCard} ${
        isEnabledStatus && styles.successCard
      } ${(isDisabledStatus || isExpiredStatus) && styles.dangerCard} ${
        isWithin30DaysStatus && styles.warningCard
      } ${className}
      `}
    >
      <div className="mx-xxl-2 my-xxl-0 my-2 mx-md-3 mx-2">
        <div className="my-xxl-3 mx-xxl-4 d-flex flex-column">
          <div className="d-flex">
            <span className={styles.cardFormLabel}>{label}</span>
            {labelTooltip && (
              <Tooltip content={labelTooltip} type="sm" className="darkTooltip">
                <AlertInfoSolidIcon
                  fontSize="sm"
                  className={styles.cardFormLabelInfoIcon}
                />
              </Tooltip>
            )}
          </div>
          <div className="d-flex align-items-center">
            {labelPreValue}
            <span
              className={`${styles.cardFormValue} card-value`}
              data-testid={`tid_smallcard_${label.replaceAll(' ', '')}`}
            >
              {value}
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}

export default function DealDetails({ deal }: { deal: AgreementApiModel }) {
  const { currentAccountId } = useSelector(accountSelector);
  const { data: accountData } = useGetAccountDataQuery(currentAccountId, {
    skip: !currentAccountId,
  });
  const { hasAccess } = usePermissions();

  const {
    isPending: isTotalsPending,
    totalSpend,
    totalSavings,
  } = useDealTotalSpend(
    deal.id,
    deal.paymentArrangement,
    deal.totalSpend,
    deal.totalSavings,
  );

  // Pay-by-User totals resolve from the deal products endpoint, so show a
  // placeholder until the first request settles. Other arrangements already have
  // their totals on the deal and render immediately.
  const formatDealMoney = (value: number) =>
    isTotalsPending ? '—' : `$${formatCurrency(value)}`;

  // check for expiration date
  const { isExpired, isExpiring } = checkExpiryStatus(deal.expiration);
  const customFormatExpirationDate = toDayMonthYear(deal.expiration);
  const pointOfContactLabel = `${deal?.primaryContact?.firstName}
    ${deal?.primaryContact?.lastName} ${deal?.primaryContact?.phoneNumber}, ${deal?.primaryContact?.email}`;

  const [showUserInviteWizard, setShowUserInviteWizard] = useState(false);
  const [showEditPointOfContact, setShowEditPointOfContact] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showEditAgreementName, setShowEditAgreementName] = useState(false);

  const accountAccessLink = buildTrainingHubUrl(currentAccountId);
  const paymentArrangement = mapPaymentArrangementText(deal);

  const atpStatusTypeId = accountData?.atpStatusTypeId;
  const showAtpStatusAlert =
    !!accountData?.isChannelEngagement &&
    !deal.enabled &&
    (atpStatusTypeId === AtpStatusType.DEACTIVATED ||
      atpStatusTypeId === AtpStatusType.DOWNGRADED);

  const atpMembershipAlertText = `Your ATP membership ${
    atpStatusTypeId === AtpStatusType.DOWNGRADED
      ? 'is now in a restricted tier'
      : 'is currently inactive'
  }. As a result, your agreements have been marked as disabled and you have forfeited the remaining unused or unredeemed products.`;
  const agreementAlertType = getAgreementAlertType({
    isExpired,
    isExpiring,
    isEnabled: deal.enabled,
    showAtpStatusAlert,
  });
  let agreementAlertText = atpMembershipAlertText;
  if (agreementAlertType === 'expired') {
    agreementAlertText = `Your agreement has expired. Please contact ${pointOfContactLabel} for options.`;
  } else if (agreementAlertType === 'expiring-soon') {
    agreementAlertText = `Your agreement is set to expire on ${toDayMonthYear(
      deal.expiration,
    )}. Please contact ${pointOfContactLabel} for options.`;
  }
  const agreementAlertVariant =
    agreementAlertType === 'expiring-soon' ? 'warning' : 'danger';
  const agreementAlertIcon =
    agreementAlertType === 'expiring-soon'
      ? AlertWarningSolidIcon
      : AlertErrorSolidIcon;

  const onAddPeopleClick = () => {
    setShowUserInviteWizard(true);
  };

  const onShowWarningPopup = () => {
    setShowWarningModal(true);
  };

  const onEditPointOfContactClick = () => {
    setShowEditPointOfContact(true);
  };

  const addPeopleButton = (
    <Button
      titleText="Add People"
      fullWidth={false}
      className={`ms-0 mt-2 mt-xxl-0 ms-xxl-2 dashboard-add-people ${
        styles.actionButton
      } ${
        isExpired ? styles.btnDisabled : styles.addPeopleBtn
      } order-1 order-xxl-2`}
      onClick={isExpiring ? onShowWarningPopup : onAddPeopleClick}
      disabled={isExpired}
    />
  );

  return (
    <>
      <div className="d-flex flex-column">
        <div className="row">
          <div className="col-12 d-flex flex-wrap align-items-center">
            <div className="d-flex flex-column flex-grow-1 homepage-agreement-name-block me-sm-4 me-0">
              <div className="d-flex flex-wrap">
                <h5
                  data-testid="tid_dealtitle"
                  className={`${styles.dealHeading} mb-0`}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {!deal.dealName ? `Agreement ID: ${deal.id}` : deal.dealName}
                </h5>
              </div>

              {deal.dealName && (
                <p className={styles.dealNameSub}>Agreement ID: {deal.id}</p>
              )}

              {hasAccess({
                permissions: [PERMISSIONS.EditDealName],
              }) && (
                <a
                  className={`ms-0 text-decoration-underline d-inline-flex ${
                    deal.enabled ? styles.cellEmail : styles.cellDisabledLink
                  } `}
                  href="#"
                  onClick={(e) => {
                    if (deal.enabled) {
                      e.preventDefault();
                      setShowEditAgreementName(true);
                    }
                  }}
                >
                  <EditIcon
                    className="align-items-start me-1 align-self-center"
                    fontSize="xs"
                  />
                  <span className={styles.actionLinks}>
                    {!deal.dealName
                      ? 'Add Agreement Name'
                      : 'Edit Agreement Name'}
                  </span>
                </a>
              )}
            </div>
            {hasAccess({
              permissions: [PERMISSIONS.ManageDealUsers],
            }) && (
              <div className="mt-1">
                {isExpired ? (
                  <Tooltip
                    content="You cannot add people to an expired Agreement."
                    type="sm"
                    placement="left"
                    className="darkTooltip"
                  >
                    <span className="d-inline-flex">{addPeopleButton}</span>
                  </Tooltip>
                ) : (
                  addPeopleButton
                )}
              </div>
            )}
          </div>
        </div>
        <div className="row gy-2 mb-3 mt-2">
          <div className="col-md-6 col-12">
            <div className="d-flex flex-column">
              <span className={styles.formLabel}>Your Point of Contact:</span>
              {deal.primaryContact ? (
                <div className="d-flex flex-wrap flex-column flex-xxl-row">
                  <span data-exclude-lg className={styles.formValue}>
                    {deal.primaryContact?.firstName}{' '}
                    {deal.primaryContact?.lastName}
                  </span>
                  <div>
                    {deal.primaryContact?.phoneNumber ? (
                      <a
                        data-exclude-lg
                        className={`ms-1 text-decoration-underline ${styles.cellEmail}`}
                        href={`tel:${deal.primaryContact?.phoneNumber}`}
                      >
                        {deal.primaryContact?.phoneNumber},
                      </a>
                    ) : (
                      ','
                    )}
                    <a
                      data-exclude-lg
                      className={`ms-1 text-decoration-underline ${styles.cellEmail}`}
                      href={`mailto:${deal.primaryContact?.email}`}
                    >
                      {deal.primaryContact?.email}
                    </a>
                  </div>
                </div>
              ) : (
                <div className="d-flex flex-wrap flex-column flex-xxl-row">
                  <span className={styles.formValue}>
                    No Point of Contact Set
                  </span>
                </div>
              )}
              {hasAccess({
                permissions: [PERMISSIONS.UpdateDealPrimaryContact],
              }) && (
                <div className="mt-1">
                  <a
                    className={`ms-0 text-decoration-underline d-inline-flex ${styles.cellEmail}`}
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      onEditPointOfContactClick();
                    }}
                  >
                    <EditIcon
                      className="align-items-start me-1 align-self-center"
                      fontSize="xs"
                    />
                    <span className={styles.actionLinks}>
                      Edit Point of Contact
                    </span>
                  </a>
                </div>
              )}
            </div>
          </div>
          <div className="col-md-6 col-12">
            <div className="d-flex flex-column">
              <span className={styles.formLabel}> Your Partner Link:</span>
              <div className="d-flex flex-column align-items-start">
                <a
                  href={accountAccessLink}
                  target="_blank"
                  rel="noreferrer noopener"
                  className={`text-decoration-underline ${styles.cellEmail}`}
                >
                  {accountAccessLink}
                </a>
                <a
                  href="#"
                  data-testid="tid_copylink"
                  className={`${styles.cellEmail} text-decoration-underline`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigator.clipboard.writeText(accountAccessLink);
                    toastWithAnalytics('Link copied successfully', 'success');
                  }}
                >
                  Copy Link
                </a>
              </div>
            </div>
          </div>
        </div>

        {agreementAlertType && (
          <div className="pb-4">
            <Alert
              variant={agreementAlertVariant}
              alertText={agreementAlertText}
              icon={agreementAlertIcon}
            />
          </div>
        )}

        <div className="row g-md-3 g-2 details-cards-block">
          <div
            className={`col-xxl-4 d-flex align-items-stretch col-6 ${styles.statusOrder}`}
          >
            <SmallCard
              label="Status"
              value={deal.enabled ? 'Enabled' : 'Disabled'}
              labelPreValue={
                deal.enabled ? (
                  <AlertSuccessSolidIcon
                    color="success"
                    fontSize="sm"
                    className={styles.statusIcon}
                  />
                ) : (
                  <AlertErrorSolidIcon
                    color="danger"
                    fontSize="sm"
                    className={styles.statusIcon}
                  />
                )
              }
            />
          </div>
          <div
            className={`col-xxl-4 d-flex align-items-stretch col-6 ${styles.createdOrder}`}
          >
            <SmallCard
              label="Created"
              value={`${toDayMonthYear(deal.created)}`}
            />
          </div>
          <div
            className={`col-xxl-4 d-flex align-items-stretch col-6 ${styles.expiresOrder}`}
          >
            {!isExpired && !isExpiring && (
              <SmallCard label="Expires" value={customFormatExpirationDate} />
            )}
            {isExpired && (
              <SmallCard
                label="Expired"
                isExpired
                value={customFormatExpirationDate}
                labelPreValue={
                  <AlertWarningSolidIcon
                    color="danger"
                    fontSize="sm"
                    className={styles.statusIcon}
                  />
                }
              />
            )}
            {isExpiring && (
              <SmallCard
                label="Expires soon"
                isExpiring
                value={customFormatExpirationDate}
                labelPreValue={
                  <AlertWarningSolidIcon
                    color="warning"
                    fontSize="sm"
                    className={styles.statusIcon}
                  />
                }
              />
            )}
          </div>
          <div
            className={`col-xxl-4 d-flex align-items-stretch col-6 ${styles.paymentOrder}`}
          >
            <SmallCard label="Payment Arrangement" value={paymentArrangement} />
          </div>
          <div
            className={`col-xxl-4 d-flex align-items-stretch col-6 ${styles.totalSpendOrder}`}
          >
            <SmallCard
              label="Total Spent"
              value={formatDealMoney(totalSpend)}
            />
          </div>
          <div
            className={`col-xxl-4 d-flex align-items-stretch col-6 ${styles.totalSavingsOrder}`}
          >
            <SmallCard
              label="Total Savings"
              value={formatDealMoney(totalSavings)}
            />
          </div>
          <div
            className={`col-xxl-4 d-flex align-items-stretch col-6 ${styles.totalRegisteredUsersOrder}`}
          >
            <SmallCard
              label="Total Registered Users"
              value={deal.totalUsersRegistered?.toString()}
            />
          </div>
          <div
            className={`col-xxl-4 d-flex align-items-stretch col-6 ${styles.totalProductsRedemeedOrder}`}
          >
            <div className="d-none d-md-block w-100">
              <SmallCard
                label="Total Products Redeemed"
                value={deal.totalProductsRedeemed?.toString()}
              />
            </div>
            <div className="d-block d-md-none w-100">
              <SmallCard
                label="Products Enrolled"
                value={deal.totalProductsRedeemed?.toString()}
              />
            </div>
          </div>
          {deal.zones?.length > 0 && (
            <div
              className={`col-xxl-4 d-flex align-items-stretch col-6 ${styles.agreementRestrictionOrder}`}
            >
              <SmallCard
                label="Zone/Country Compatibility"
                labelTooltip={`Users enrolling in products within this agreement must reside in a country located in ${printableZonesLabel(
                  deal,
                ).replace('Countries', 'countries')}`}
                value={printableZoneRestrictionLabel(deal)}
                className={styles.restrictionCard}
              />
            </div>
          )}
        </div>
        <div className="mt-xxl-2">
          <div className="row mt-3 mt-xxl-4">
            <div className="col">
              <Card className={styles.productDetailsCard}>
                <div className="row gy-3 my-3 mx-3 align-items-end">
                  <div className="col-12 col-xxl-auto my-0 px-0 flex-fill">
                    <h6 className={styles.productDetailsTitle}>
                      Product Details
                    </h6>
                    <p className={styles.productDetailsDesc}>
                      Review the details, pricing, and redemption totals of all
                      products available in this agreement.
                    </p>
                  </div>
                </div>
                <div className="row row-cols-1">
                  <div className="col d-md-block d-none">
                    <hr className="m-0 p-0" />
                  </div>
                  <div className="col mt-0">
                    <div className="mx-3">
                      <div className="d-none d-md-block overflow-auto deal-products-table-container">
                        <DealProductsTable deal={deal} />
                      </div>
                      <div className="d-md-none">
                        <DealProductCards deal={deal} />
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
        <div>
          <Addendums />
        </div>
      </div>
      {showWarningModal && (
        <ExpireWarningModal
          deal={deal}
          onConfirm={() => setShowUserInviteWizard(true)}
          onCancel={() => setShowWarningModal(false)}
        />
      )}
      {showUserInviteWizard && (
        <InviteWizard
          deal={deal}
          step="product"
          onCancel={() => setShowUserInviteWizard(false)}
        />
      )}
      {showEditPointOfContact && (
        <EditPointOfContactModal
          accountId={currentAccountId}
          deal={deal}
          onCancel={() => setShowEditPointOfContact(false)}
        />
      )}
      {showEditAgreementName && (
        <EditAgreementNameModal
          accountId={currentAccountId}
          deal={deal}
          onCancel={() => setShowEditAgreementName(false)}
        />
      )}
    </>
  );
}
